import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type AAThemeContextType = {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
};

const applyColorMode = (isDarkMode: boolean) => {
  document.documentElement.setAttribute(
    "data-bs-theme",
    isDarkMode ? "dark" : "light"
  );
};

const getColorModeMediaQuery = () => {
  return window.matchMedia("(prefers-color-scheme: dark)");
};

// Use the browser default color mode until we are authenticated
export const initColorMode = () => {
  const colorModeMediaQuery = getColorModeMediaQuery();
  applyColorMode(colorModeMediaQuery.matches);
};

const AAThemeContext = createContext<AAThemeContextType | null>(null);

const useAAThemeState = ({
  darkModeStoredPreference,
  updateDarkModeStoredPreference,
}: {
  darkModeStoredPreference: boolean | null;
  updateDarkModeStoredPreference: (darkMode: boolean) => void;
}) => {
  const colorModeMediaQuery = getColorModeMediaQuery();

  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  const toggleDarkMode = () => {
    applyColorMode(!isDarkMode);
    setIsDarkMode(!isDarkMode);
    // Only set localStorage when the user explicitly toggles the dark mode.
    updateDarkModeStoredPreference(!isDarkMode);
  };

  useEffect(() => {
    // Default dark mode to browser preference, unless overridden by localStorage.
    const browserDefault = colorModeMediaQuery.matches || false;
    const initialDarkMode =
      darkModeStoredPreference !== null
        ? darkModeStoredPreference
        : browserDefault;
    applyColorMode(initialDarkMode);
    setIsDarkMode(initialDarkMode);

    const handleBrowserColorModeChange = (
      event: MediaQueryListEvent | MediaQueryList
    ) => {
      // If localStorage is not set, apply the updated browser color mode.
      if (darkModeStoredPreference === null) {
        applyColorMode(event.matches);
        setIsDarkMode(event.matches);
      }
    };

    colorModeMediaQuery.addEventListener(
      "change",
      handleBrowserColorModeChange
    );

    return () => {
      colorModeMediaQuery.removeEventListener(
        "change",
        handleBrowserColorModeChange
      );
    };
  }, []);

  return { isDarkMode, toggleDarkMode };
};

export const useAATheme = () => {
  const context = useContext(AAThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within an AAThemeProvider");
  }
  return context;
};

type AAThemeProviderProps = {
  children: ReactNode;
  darkModeStoredPreference: boolean | null;
  updateDarkModeStoredPreference: (darkMode: boolean) => void;
};

export const AAThemeProvider = ({
  children,
  darkModeStoredPreference,
  updateDarkModeStoredPreference,
}: AAThemeProviderProps) => {
  const aaThemeState = useAAThemeState({
    darkModeStoredPreference,
    updateDarkModeStoredPreference,
  });
  return (
    <AAThemeContext.Provider value={aaThemeState}>
      {children}
    </AAThemeContext.Provider>
  );
};
